import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiClientError } from "@/api/client";
import {
  KbDuplicateExisting,
  KbFolder,
  createKbPage,
  newKbRequestId,
} from "@/api/knowledge";
import { KbDuplicateDialog } from "./KbDuplicateDialog";

/**
 * D-147 W1 S4 — «+ Сторінка» (створення ЛИШЕ з розділу, §6/P8): POST create
 * → draft-item (rev=1) → редактор (content_image привʼязується вже до
 * itemId). Дублікат назви → єдиний duplicate-діалог; confirm-retry під ТИМ
 * САМИМ requestId (ledger re-arm failed-рядка, Trap-64-сумісно).
 */
export function KbCreatePageDialog({
  sectionId,
  folders,
  onClose,
}: {
  sectionId: string;
  folders: KbFolder[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [duplicate, setDuplicate] = useState<KbDuplicateExisting | null>(null);
  const [requestId] = useState(newKbRequestId);

  const submit = (confirmDuplicateTitle: boolean) => {
    const trimmed = title.trim();
    setTouched(true);
    if (trimmed === "") return;
    setBusy(true);
    setError(null);
    createKbPage(sectionId, {
      title: trimmed,
      folderId: folderId || null,
      requestId,
      confirmDuplicateTitle,
    })
      .then((r) => navigate(`/knowledge/items/${r.itemId}/edit`))
      .catch((e: unknown) => {
        if (e instanceof ApiClientError && e.status === 409) {
          const details = (e.details ?? {}) as { code?: string; existing?: KbDuplicateExisting };
          if (details.code === "duplicate_title" || e.code === "duplicate_title") {
            setDuplicate(details.existing ?? { itemId: "", title: "" });
            return;
          }
        }
        setError(e instanceof Error ? e.message : "Не вдалося створити сторінку.");
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Нова сторінка"
      >
        <div className="w-full max-w-md rounded-xl border bg-card p-5 shadow-2xl">
          <div className="text-sm font-semibold">Нова сторінка</div>
          <div className="mt-4">
            <label className="mb-1 block text-xs font-semibold" htmlFor="kb-np-title">
              Назва матеріалу <span className="text-destructive">*</span>
            </label>
            <input
              id="kb-np-title"
              value={title}
              autoFocus
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
            {touched && title.trim() === "" && (
              <p role="alert" className="mt-1 text-xs text-destructive">
                Порожня назва.
              </p>
            )}
          </div>
          <div className="mt-3">
            <label className="mb-1 block text-xs font-semibold" htmlFor="kb-np-folder">
              Папка
            </label>
            <select
              id="kb-np-folder"
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">— без папки —</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          {error && (
            <p role="alert" className="mt-3 text-xs text-destructive">
              {error}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3.5 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              Скасувати
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => submit(false)}
              className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {busy ? "Створюємо…" : "Створити чернетку"}
            </button>
          </div>
        </div>
      </div>

      {duplicate !== null && (
        <KbDuplicateDialog
          kind="title"
          existing={duplicate.itemId ? duplicate : null}
          confirmLabel="Створити як окремий"
          busy={busy}
          onCancel={() => setDuplicate(null)}
          onConfirm={() => {
            setDuplicate(null);
            submit(true);
          }}
        />
      )}
    </>
  );
}
